// Free, local tag suggestions from the video's text (title, description,
// channel). Only returns values that exist in the current taxonomies.

import { Taxonomies, TaxonomyKey } from "./taxonomies";

export interface TagSuggestion {
  value: string;
  /** 0–1, higher = more signals matched. */
  confidence: number;
  reason: string;
}

export type Suggestions = Record<TaxonomyKey, TagSuggestion[]>;

// Keywords (FR + EN) for the default taxonomy values. Custom values added in
// the CMS are matched on their own name.
const KEYWORDS: Record<string, RegExp[]> = {
  // Formats
  "Ad": [/\bads?\b/, /\bpub(licit[ée])?\b/, /\bcommercial\b/, /\bspot\b/, /\bcampaign\b/, /\bcampagne\b/],
  "Explainer": [/\bexplainer\b/, /\bexplained?\b/, /\bhow it works\b/, /\bcomment [çc]a marche\b/, /\bexplicati/],
  "Feature launch": [/\bintroducing\b/, /\blaunch(ing|es)?\b/, /\blancement\b/, /\bnew feature\b/, /\bnouvelle fonctionnalit/, /\bannounc/],
  "Update": [/\bupdate\b/, /\brelease notes?\b/, /\bwhat'?s new\b/, /\bnouveaut[ée]s\b/, /\bv\d+(\.\d+)+\b/],
  "Product demo": [/\bdemo\b/, /\bd[ée]mo\b/, /\bwalkthrough\b/, /\bproduct tour\b/, /\bdemonstration\b/],
  "Showreel": [/\bshowreel\b/, /\breel\b/, /\bdemo ?reel\b/],
  "Product presentation": [/\bpresent(ation|ing)\b/, /\bpr[ée]sentation\b/, /\bmeet\b/, /\bdiscover\b/, /\bd[ée]couvr/],
  // Industries
  "SaaS": [/\bsaas\b/, /\bb2b\b/, /\bsoftware\b/, /\bplatform\b/, /\bworkspace\b/, /\bdashboard\b/, /\bapi\b/],
  "Finance": [/\bfinan/, /\bfintech\b/, /\bpayments?\b/, /\bpaiements?\b/, /\binvest/, /\baccounting\b/, /\bcomptab/, /\binvoic/, /\bfactur/],
  "Cryptos": [/\bcrypto/, /\bblockchain\b/, /\bweb3\b/, /\bbitcoin\b/, /\bethereum\b/, /\bdefi\b/, /\bnft\b/, /\bwallet\b/],
  "AI": [/\bai\b/, /\bia\b/, /\bartificial intelligence\b/, /\bintelligence artificielle\b/, /\bgpt\b/, /\bllm\b/, /\bmachine learning\b/, /\bagents?\b/, /\bcopilot\b/],
  "Health": [/\bhealth/, /\bsant[ée]\b/, /\bmedic/, /\bm[ée]dic/, /\bpatient/, /\bwellness\b/, /\bfitness\b/, /\bclinic/],
  "Gaming": [/\bgam(e|es|ing)\b/, /\bjeux?\b/, /\besport/, /\bplaystation\b/, /\bxbox\b/, /\bnintendo\b/],
  "French tech": [/\bfrench ?tech\b/, /\bmade in france\b/, /\bstartup fran[çc]aise\b/],
  "Y Combinator": [/\by ?combinator\b/, /\byc\s?[swf]\d{2}\b/, /\(yc [swf]\d{2}\)/],
  "Environment": [/\bclimate\b/, /\bclimat\b/, /\bcarbon\b/, /\bcarbone\b/, /\bsustainab/, /\bdurab/, /\benvironment/, /\bgreen\b/, /\benergy\b/, /\b[ée]nergie\b/],
  "Bank": [/\bbank/, /\bbanque\b/, /\bneobank\b/, /\bcard\b/, /\bcarte bancaire\b/, /\bcompte pro\b/],
  "Agency": [/\bagency\b/, /\bagence\b/],
  "Food": [/\bfood\b/, /\bfoodtech\b/, /\brestaurant/, /\brecipe/, /\brecette/, /\bcuisine\b/, /\bdelivery\b/, /\blivraison\b/, /\bgrocer/],
  "Freelance": [/\bfreelanc/, /\bind[ée]pendants?\b/],
  "CRM": [/\bcrm\b/, /\bsales pipeline\b/, /\bcustomer relationship\b/, /\bprospect/, /\bleads?\b/],
  "Rebranding": [/\brebrand/, /\bnew brand\b/, /\bnouvelle identit/, /\bbrand identity\b/, /\bnew logo\b/, /\bnouveau logo\b/],
  "Tutorial": [/\btutorial\b/, /\btuto(riel)?\b/, /\bhow to\b/, /\bguide\b/, /\bstep by step\b/, /\b[ée]tape par [ée]tape\b/],
  "Use Case": [/\buse cases?\b/, /\bcas d'usage\b/, /\bcustomer story\b/, /\bcase study\b/, /\bt[ée]moignage\b/],
  // Styles
  "2D": [/\b2d\b/, /\bcel animation\b/, /\bcharacter animation\b/, /\billustrat/],
  "3D": [/\b3d\b/, /\bcgi\b/, /\bblender\b/, /\bcinema ?4d\b/, /\bc4d\b/, /\bhoudini\b/, /\bredshift\b/, /\boctane\b/],
  "Flat Design": [/\bflat\b/, /\bvector\b/, /\bvectoriel/],
  "Gradient": [/\bgradients?\b/, /\bd[ée]grad[ée]s?\b/],
  "Motion & Video": [/\blive action\b/, /\bfootage\b/, /\bprise de vue\b/, /\bmixed media\b/, /\bvideo ?production\b/],
  "Collage": [/\bcollage\b/, /\bcut ?out\b/, /\bpaper\b/, /\bpapier\b/, /\bstop ?motion\b/],
  "Fake 3D": [/\bfake 3d\b/, /\b2\.5d\b/, /\bisometric\b/, /\bisom[ée]trique\b/],
};

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function suggestFromText(
  taxonomies: Taxonomies,
  input: { title?: string; description?: string; authorName?: string; tags?: string[] },
): Suggestions {
  const title = (input.title ?? "").toLowerCase();
  const body = [input.description, input.authorName, ...(input.tags ?? [])]
    .filter(Boolean)
    .join(" \n ")
    .toLowerCase();

  const scoreValue = (value: string): TagSuggestion | null => {
    const patterns = KEYWORDS[value] ?? [new RegExp(`\\b${escapeRegExp(value.toLowerCase())}\\b`)];
    let score = 0;
    const hits: string[] = [];
    patterns.forEach((re) => {
      const inTitle = title.match(re);
      const inBody = body.match(re);
      if (inTitle) {
        score += 2;
        hits.push(`« ${inTitle[0].trim()} » dans le titre`);
      } else if (inBody) {
        score += 1;
        hits.push(`« ${inBody[0].trim()} » dans la description`);
      }
    });
    if (!score) return null;
    return { value, confidence: Math.min(0.95, 0.35 + score * 0.15), reason: hits.slice(0, 2).join(", ") };
  };

  const run = (key: TaxonomyKey) =>
    taxonomies[key]
      .map(scoreValue)
      .filter((s): s is TagSuggestion => !!s)
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 3);

  return { formats: run("formats"), industries: run("industries"), styles: run("styles") };
}

/** Merges suggestion lists, keeping the highest confidence per value. */
export function mergeSuggestions(...lists: TagSuggestion[][]): TagSuggestion[] {
  const best = new Map<string, TagSuggestion>();
  lists.flat().forEach((s) => {
    const current = best.get(s.value);
    if (!current || s.confidence > current.confidence) best.set(s.value, s);
  });
  return Array.from(best.values()).sort((a, b) => b.confidence - a.confidence);
}
